/**
 * What the admin is shown before an account is deleted.
 *
 * The deletion request itself is written by an unauthenticated public page, so
 * the phone, email and reason on it are whatever the requester typed. Approving
 * a *request* therefore means approving attacker-typed text. The admin has to
 * approve an ACCOUNT instead: this module turns a phone lookup into the real
 * account's name, email, number and join date, which is something an admin has
 * a chance of recognising as wrong.
 *
 * Pure on purpose — the Firestore query lives in the dialog, everything that
 * decides what is shown and whether approval is even offered lives here.
 */

/** The identity summary shown in the confirmation dialog. */
export interface AccountSummary {
  uid: string;
  name: string | null;
  email: string | null;
  /** The phone stored ON THE ACCOUNT — not the one typed into the request. */
  phone: string | null;
  createdAt: Date | null;
  isAdmin: boolean;
}

export type TargetResolution =
  | { ok: true; account: AccountSummary }
  | { ok: false; reason: 'no-match' | 'ambiguous-match' | 'lookup-failed'; candidateUids: string[] };

/** Whether the requester's ownership of the number was ever actually proven. */
export interface PhoneOwnership {
  /** True only when the server verified a proof. Nothing produces one today. */
  proven: boolean;
  /** The request said so, but said so is not proof — the collection is public. */
  claimed: boolean;
  method: string | null;
}

/** Firestore Timestamp | Date | epoch millis | ISO string -> Date | null. */
export function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && typeof (value as { toDate?: unknown }).toDate === 'function') {
    const d = (value as { toDate: () => Date }).toDate();
    return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function firstString(...values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Build the display name from whichever of the app's several name shapes the
 * document actually has. A blank name is reported as null so the dialog can say
 * "no name on this account" rather than render an empty line the admin reads
 * straight past.
 */
export function accountDisplayName(data: Record<string, unknown>): string | null {
  const direct = firstString(data.name, data.displayName, data.fullName);
  if (direct) return direct;

  const en = [data.firstNameEn, data.lastNameEn]
    .filter((p): p is string => typeof p === 'string' && !!p.trim())
    .join(' ')
    .trim();
  if (en) return en;

  const ar = [data.firstNameAr, data.lastNameAr]
    .filter((p): p is string => typeof p === 'string' && !!p.trim())
    .join(' ')
    .trim();
  return ar || null;
}

/** True when the account holds an ACTIVE admin role. The callable refuses these. */
export function holdsActiveAdminRole(data: Record<string, unknown>): boolean {
  const roles = Array.isArray(data.roles) ? data.roles : [];
  return roles.some((r) => {
    if (!r || typeof r !== 'object') return false;
    const role = r as { role?: unknown; isActive?: unknown };
    return role.role === 'admin' && role.isActive !== false;
  });
}

export function summarizeAccount(uid: string, data: Record<string, unknown>): AccountSummary {
  return {
    uid,
    name: accountDisplayName(data),
    email: firstString(data.email),
    phone: firstString(data.phone, data.phoneNumber),
    createdAt: toDate(data.createdAt),
    isAdmin: holdsActiveAdminRole(data),
  };
}

/**
 * Exactly one match or nothing. Zero and several are both refusals — the
 * callable refuses them too, and offering an Approve button the server will
 * reject just teaches admins to click through errors.
 */
export function resolveTarget(
  candidates: ReadonlyArray<{ uid: string; data: Record<string, unknown> }>,
): TargetResolution {
  const seen = new Map<string, Record<string, unknown>>();
  for (const c of candidates || []) {
    const uid = typeof c?.uid === 'string' ? c.uid.trim() : '';
    if (uid) seen.set(uid, c.data || {});
  }
  const uids = Array.from(seen.keys());
  if (uids.length === 0) return { ok: false, reason: 'no-match', candidateUids: [] };
  if (uids.length > 1) return { ok: false, reason: 'ambiguous-match', candidateUids: uids };
  return { ok: true, account: summarizeAccount(uids[0], seen.get(uids[0]) as Record<string, unknown>) };
}

/**
 * Read the phone-ownership verdict off the request.
 *
 * `deletion.phoneOwnership` is written by the server, which is the only party
 * that can evaluate a proof. Anything else on the document — including a
 * `phoneOwnershipProven: true` an attacker wrote — is at most a CLAIM.
 */
export function readPhoneOwnership(req: Record<string, unknown> | null | undefined): PhoneOwnership {
  const request = req && typeof req === 'object' ? req : {};
  const deletion = (request as { deletion?: unknown }).deletion;
  const evaluated = deletion && typeof deletion === 'object'
    ? (deletion as { phoneOwnership?: unknown }).phoneOwnership
    : null;

  const claimed = (request as { phoneOwnershipProven?: unknown }).phoneOwnershipProven === true;

  if (evaluated && typeof evaluated === 'object') {
    const e = evaluated as { proven?: unknown; method?: unknown; claimed?: unknown };
    return {
      proven: e.proven === true,
      claimed: e.claimed === true || claimed,
      method: typeof e.method === 'string' ? e.method : null,
    };
  }
  return { proven: false, claimed, method: null };
}

/**
 * The uid a previous run of the cascade was pinned to, if any.
 *
 * Forgeable like every other field, which is why the server re-checks it against
 * its own signed marker. It is used only to offer the admin a resume path when
 * the account is already gone and the phone therefore resolves to nobody.
 */
export function resumeUidFromRequest(req: Record<string, unknown> | null | undefined): string | null {
  const deletion = req && typeof req === 'object' ? (req as { deletion?: unknown }).deletion : null;
  if (!deletion || typeof deletion !== 'object') return null;
  const uid = (deletion as { uid?: unknown }).uid;
  return typeof uid === 'string' && uid.trim() ? uid.trim() : null;
}

/**
 * The uid the Approve button will send as `confirmUid`, or null when there is
 * nothing safe to confirm.
 */
export function confirmableUid(
  resolution: TargetResolution,
  req: Record<string, unknown> | null | undefined,
): string | null {
  if (resolution.ok) return resolution.account.isAdmin ? null : resolution.account.uid;
  // Only a request whose own run already pinned a uid may be resumed, and only
  // when the phone resolves to nobody (the account is gone) rather than to
  // several people (a duplicate that must be fixed first).
  if (resolution.reason === 'no-match') return resumeUidFromRequest(req);
  return null;
}
